import React, { useEffect, useState } from 'react';
import { Header } from './components/header';
import { Hero } from './components/hero';
import { NewsCard } from './components/newscard';
import { GamesSection } from './components/games';
import { Footer } from './components/footer';
import { fetchGamingNews } from './services/newsService';
import { NewsItem } from './types';

const App: React.FC = () => {
  const [news, setNews] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Paginación para el bloque de "Últimas Noticias"
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 6;

  useEffect(() => {
    const loadData = async () => {
      try {
        const data = await fetchGamingNews();
        setNews(data);
      } catch (e) {
        console.error("Failed to load content", e);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // 1. Hero: Las 5 más recientes para el carrusel
  const heroSlides = news.length > 0 ? news.slice(0, 5) : [];

  // 2. Grid: Todas las noticias disponibles para la lista paginada
  const allGridNews = news;

  // Paginación Grid Principal
  const totalPages = Math.ceil(allGridNews.length / itemsPerPage);
  const displayedGridNews = allGridNews.slice(currentPage * itemsPerPage, (currentPage + 1) * itemsPerPage);

  const nextPage = () => setCurrentPage((p) => (p + 1) % totalPages);
  const prevPage = () => setCurrentPage((p) => (p - 1 + totalPages) % totalPages);

  return (
    <div className="min-h-screen bg-[#0f172a] text-slate-200 selection:bg-blue-500/30">
      <Header />

      <main className="pt-24">
        {/* HERO (Carrusel) */}
        {loading ? (
          <div className="w-full h-[85vh] bg-slate-800 animate-pulse mb-12"></div>
        ) : (
          <Hero news={heroSlides} />
        )}

        {/* SECCIÓN: ÚLTIMAS NOTICIAS (Todas mezcladas) */}
        <section className="max-w-7xl mx-auto px-6 mb-24 scroll-mt-24" id="noticias">
          <div className="flex items-center justify-between mb-10">
            <h2 className="text-3xl font-bold text-white">Últimas Noticias</h2>
            {!loading && allGridNews.length > 6 && (
              <div className="flex gap-2">
                <button onClick={prevPage} className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center hover:bg-white/5 transition-colors"><span className="text-xl">←</span></button>
                <button onClick={nextPage} className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center hover:bg-white/5 transition-colors"><span className="text-xl">→</span></button>
              </div>
            )}
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(6)].map((_, i) => <div key={i} className="h-96 bg-slate-800/50 rounded-3xl animate-pulse border border-white/5"></div>)}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {displayedGridNews.map((item) => (
                <NewsCard key={item.id} news={item} />
              ))}
            </div>
          )}
          
          {!loading && totalPages > 1 && (
             <div className="mt-6 text-center text-sm text-slate-500">Página {currentPage + 1} de {totalPages}</div>
          )}
        </section>

        {/* SECCIONES POR CONSOLA (SIN LÍMITES) */}
        {['PlayStation', 'Xbox', 'Nintendo', 'General'].map((consoleName) => {
          
          // CORRECCIÓN: Quitamos el .slice(). Ahora muestra TODAS las de esa categoría.
          const consoleNews = news.filter(item => item.platform === consoleName);

          // Si no hay noticias de esta consola, ocultamos la sección entera
          if (consoleNews.length === 0) return null;

          return (
            <section key={consoleName} className="max-w-7xl mx-auto px-6 mb-16 scroll-mt-24" id={consoleName.toLowerCase()}>
              <div className="flex items-center gap-3 mb-8">
                 <div className={`w-3 h-8 rounded-full ${
                      consoleName === 'PlayStation' ? 'bg-blue-600' :
                      consoleName === 'Xbox' ? 'bg-green-500' :
                      consoleName === 'Nintendo' ? 'bg-red-500' :
                      consoleName === 'PC' ? 'bg-yellow-500' : 'bg-slate-500'
                    }`}></div>
                <h2 className="text-2xl font-bold text-white uppercase tracking-wider">{consoleName}</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {consoleNews.map((item) => (
                  <NewsCard key={item.id} news={item} />
                ))}
              </div>
            </section>
          );
        })}

        {/* JUEGOS */}
        <div id="juegos" className="scroll-mt-24">
          <GamesSection />
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default App;